<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import { isEmpty, isNil } from 'lodash';

import { useYardTaskPresenterV2 } from '@satellite/composables/useYardTaskPresenterV2';
import { useDebouncedValue } from '@satellite/composables/useDebouncedValue';

import { useCheckTaskPanel } from '@/composables/useCheckTaskPanel';
import { emitCustomEvent } from '@/composables';
import { useYardInventory } from '@/composables/useYardInventory';
import { filterCheckOutcomesByAsset } from '@/helpers/latestCheckOutcome';
import YardInventoryAssetRow from '@/components/YardInventoryAssetRow.vue';
import YardInventoryFilterDialog from '@/components/YardInventoryFilterDialog.vue';

import type { CheckTaskCheckAssetContainerView, CheckTaskViewData } from '@nova/core';
import type { InventoryAssetRow } from '@/composables/useYardInventory';

const { t } = useI18n();
const { xs } = useDisplay();

const { dialogProps, handleAfterLeave, buildControls } = useCheckTaskPanel(
  'open-check-spot-area-inventory'
);

const taskId = computed(() => dialogProps.value?.taskId ?? '');
const checkId = computed(() => dialogProps.value?.checkId ?? '');
const warehouseId = computed(() => dialogProps.value?.warehouseId ?? '');
const taskStatus = computed(() => dialogProps.value?.taskStatus);

const { isOpen, closePanel } = buildControls(() => {
  // no-op; the presenter auto-fetches once the dialog provides the task id.
});

// `live` mode keeps the task synced via EventHub; fetching is gated by `taskId`,
// which is empty while the panel is closed.
const taskPresenter = useYardTaskPresenterV2({ taskId, headless: true });

// This panel only opens for warehouse-scoped inventory check tasks.
const liveTask = computed<CheckTaskViewData | null>(
  () => (taskPresenter.data.value as CheckTaskViewData | undefined) ?? null
);

/** Prefer live `checkAssetContainers` from the refetched task; fall back to event payload. */
const liveCheckAssetContainers = computed<CheckTaskCheckAssetContainerView[] | undefined>(
  () => liveTask.value?.checkAssetContainers ?? dialogProps.value?.checkAssetContainers
);

const inventory = useYardInventory({
  taskId,
  warehouseId,
  headless: true
});

/**
 * Debounce the search input so the nested bucket/row filter only runs once the user pauses
 * typing — keeps the list responsive when the warehouse holds hundreds of containers.
 */
const SEARCH_DEBOUNCE_MS = 200;
// Vuetify's `clearable` emits `null` when the user clicks the X; the inventory composable's
// `search` ref must stay a string because it calls `.trim().toLowerCase()` downstream.
const searchInput = ref<string | null>('');
const { debouncedValue: debouncedSearchInput, cancel: cancelSearchUpdate } = useDebouncedValue(
  searchInput,
  SEARCH_DEBOUNCE_MS
);

watch(debouncedSearchInput, debouncedSearchValue => {
  inventory.search.value = debouncedSearchValue ?? '';
});

function onAfterLeave(): void {
  cancelSearchUpdate();
  searchInput.value = '';
  inventory.search.value = '';
  handleAfterLeave();
}

const showFilterDialog = ref(false);

function applyFilter(next: Set<string>): void {
  inventory.filterSelection.value = next;
}

const selectedBucketCount = computed(() => inventory.filterSelection.value.size);
const isSearching = computed(() => !isEmpty(inventory.search.value.trim()));

const statusCards = computed(() => [
  {
    key: 'pending',
    icon: 'mdi-clipboard-outline',
    color: 'neutral-80',
    label: t('yardInventory.counters.pending'),
    count: inventory.warehouseCounts.value.pending
  },
  {
    key: 'checked',
    icon: 'mdi-clipboard-check-outline',
    color: 'success',
    label: t('yardInventory.counters.checked'),
    count: inventory.warehouseCounts.value.checked
  },
  {
    key: 'missing',
    icon: 'mdi-clipboard-remove-outline',
    color: 'error',
    label: t('yardInventory.counters.missing'),
    count: inventory.warehouseCounts.value.missing
  }
]);

function onRowClick(row: InventoryAssetRow): void {
  const status = taskStatus.value;
  // Bail if the wizard contract can't be satisfied; `status` feeds downstream YardTaskStatus checks.
  if (
    isEmpty(taskId.value) ||
    isEmpty(checkId.value) ||
    isEmpty(warehouseId.value) ||
    isNil(status)
  ) {
    return;
  }
  emitCustomEvent('open-asset-check-wizard', {
    taskId: taskId.value,
    checkId: checkId.value,
    assetContainerId: row.assetContainer.id,
    warehouseId: warehouseId.value,
    taskStatus: status,
    checkAssetContainers: filterCheckOutcomesByAsset(
      liveCheckAssetContainers.value,
      row.assetContainer.id
    )
  });
}
</script>

<template>
  <v-dialog
    v-model="isOpen"
    data-testid="yard-inventory-panel"
    fullscreen
    scroll-strategy="none"
    @keydown.escape="closePanel"
    @after-leave="onAfterLeave">
    <v-card class="yard-inventory-panel d-flex flex-column h-100 bg-surface rounded-0">
      <div class="yard-inventory-panel__header pa-4 d-flex flex-column gap-3">
        <div class="d-flex align-center gap-3">
          <v-btn
            size="small"
            icon
            variant="text"
            :aria-label="t('yardInventory.header.back')"
            data-testid="yard-inventory-back"
            @click="closePanel">
            <v-icon icon="mdi-arrow-left" />
          </v-btn>
          <h3 class="typography-heading-lg-bold flex-fill ma-0">
            {{ t('yardInventory.header.title') }}
          </h3>
        </div>

        <div class="d-flex align-center ga-2">
          <v-card
            v-for="card in statusCards"
            :key="card.key"
            variant="outlined"
            rounded
            class="status-card flex-1-1-0 bg--color-background-primary py-2 px-3"
            :data-testid="`yard-inventory-counter-${card.key}`">
            <div class="d-flex align-center ga-2">
              <v-icon size="20" :color="card.color" :icon="card.icon" />
              <span class="d-none d-sm-inline typography-number-md text-medium-emphasis">
                {{ card.label }}
              </span>
              <span class="typography-heading-md-bold text--color-text-secondary ms-auto">
                {{ card.count }}
              </span>
            </div>
          </v-card>
          <v-spacer />
          <v-chip
            pill
            size="large"
            variant="tonal"
            color="primary"
            data-testid="yard-inventory-filter-chip"
            @click="showFilterDialog = true">
            <span class="typography-body-sm-bold text--color-text-secondary">
              {{ t('yardInventory.header.filterChip', { count: selectedBucketCount }) }}
            </span>
            <v-icon class="ms-1" icon="mdi-menu-down" size="20" />
          </v-chip>
        </div>

        <v-text-field
          v-model="searchInput"
          variant="outlined"
          density="comfortable"
          base-color="neutral-40"
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          :placeholder="t('yardInventory.search.placeholder')"
          data-testid="yard-inventory-search" />
      </div>

      <v-divider />

      <div class="flex-fill overflow-y-auto bg--color-background-primary">
        <div
          v-if="inventory.isLoading.value"
          class="pa-8 d-flex align-center justify-center"
          data-testid="yard-inventory-loading">
          <v-progress-circular indeterminate size="32" color="primary" />
        </div>

        <template v-else-if="isEmpty(inventory.visibleBuckets.value)">
          <div
            class="pa-8 d-flex flex-column align-center justify-center text-center gap-2"
            data-testid="yard-inventory-empty">
            <v-icon size="32" icon="mdi-help-circle-outline" class="text--color-text-tertiary" />
            <span class="typography-body-md text--color-text-secondary">
              {{ t('yardInventory.empty.title') }}
            </span>
            <span class="typography-caption text--color-text-tertiary">
              {{ t('yardInventory.empty.subtitle') }}
            </span>
          </div>
        </template>

        <template v-else>
          <section
            v-for="bucket in inventory.visibleBuckets.value"
            :key="bucket.key"
            class="bucket"
            :data-testid="`yard-inventory-bucket-${bucket.key}`">
            <div class="bucket__header d-flex align-center justify-space-between px-4 py-2">
              <div class="d-flex align-center gap-2">
                <v-icon
                  v-if="bucket.kind === 'missing' || bucket.kind === 'waiting'"
                  size="16"
                  icon="mdi-pin"
                  class="text--color-text-tertiary" />
                <span class="typography-body-sm-bold text-uppercase text--color-text-tertiary">
                  <template v-if="bucket.kind === 'missing'">
                    {{ t('yardInventory.sections.missing') }}
                  </template>
                  <template v-else-if="bucket.kind === 'waiting'">
                    {{ t('yardInventory.sections.waitingAssignment') }}
                  </template>
                  <template v-else>{{ bucket.name }}</template>
                </span>
              </div>
              <span class="typography-caption text--color-text-tertiary">
                {{ t('yardInventory.assetCount', { count: bucket.rows.length }) }}
              </span>
            </div>
            <div class="bucket__rows d-flex flex-column ga-2 px-4 pb-3">
              <yard-inventory-asset-row
                v-for="row in bucket.rows"
                :key="row.assetContainer.id"
                :row="row"
                @click="onRowClick" />
              <div
                v-if="!bucket.rows.length"
                class="typography-body-sm text--color-text-tertiary py-2"
                :data-testid="`yard-inventory-bucket-${bucket.key}-empty`">
                {{
                  t(
                    isSearching
                      ? 'yardInventory.bucket.noAssetsFound'
                      : 'yardInventory.bucket.noAssets'
                  )
                }}
              </div>
            </div>
          </section>
        </template>
      </div>

      <v-divider />

      <div
        class="yard-inventory-panel__footer pa-3 d-flex ga-3"
        :class="{ 'flex-column-reverse': xs }">
        <v-btn
          variant="outlined"
          size="x-large"
          :class="['typography-button-lg', xs ? 'w-100' : 'flex-fill']"
          prepend-icon="mdi-arrow-left"
          data-testid="yard-inventory-back-to-task"
          @click="closePanel">
          {{ t('yardInventory.footer.backToTask') }}
        </v-btn>
        <v-btn
          variant="elevated"
          color="primary"
          size="x-large"
          :class="['typography-button-lg', xs ? 'w-100' : 'flex-fill']"
          prepend-icon="mdi-plus"
          disabled
          data-testid="yard-inventory-new-asset">
          {{ t('yardInventory.footer.newAsset') }}
        </v-btn>
      </div>

      <yard-inventory-filter-dialog
        v-model="showFilterDialog"
        :buckets="inventory.filterBuckets.value"
        :selection="inventory.filterSelection.value"
        @apply="applyFilter" />
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.yard-inventory-panel__header {
  background: $color-neutral-0;
  position: sticky;
  top: 0;
  z-index: 2;
}

.bucket__header {
  background: rgb(var(--v-theme-neutral-10));
}

.yard-inventory-panel__footer {
  background: $color-neutral-0;
  position: sticky;
  bottom: 0;
  z-index: 2;
}

.status-card {
  --v-border-color: var(--v-theme-neutral-40);
  --v-border-opacity: 1;
  border-color: rgb(var(--v-theme-neutral-40)) !important;
}
</style>
